import { useState } from 'react';

import { ProgressSpinner } from 'primereact/progressspinner';
import { Splitter, SplitterPanel } from 'primereact/splitter';
import { Controller } from 'react-hook-form';

import { useAspirationForm } from '../hooks/useAspirationForm';
import { AspirationHistoryGrid } from './AspirationHistoryGrid';
import { AspirationInfoPanel } from './AspirationInfoPanel';
import { AspirationToolbar } from './AspirationToolbar';
import { CommentsSection } from './CommentsSection';
import { DonorScheduleGrid } from './DonorScheduleGrid';
import { EmbGrid } from './EmbGrid';
import { OvaryFieldsSection } from './OvaryFieldsSection';
import { ProcedureGrid } from './ProcedureGrid';
import { TopFieldsSection } from './TopFieldsSection';

import './AspirationForm.css';

type AspirationFormProps = {
  setShowForm: (value: boolean) => void;
};

const SPLITTER_PANEL_SIZE = 80;

export const AspirationForm = ({ setShowForm }: AspirationFormProps) => {
  const h = useAspirationForm(setShowForm);
  const [openWithCommentType, setOpenWithCommentType] = useState<
    string | undefined
  >();

  const handlePregnantChange = (checked: boolean, previousValue: boolean) => {
    if (checked && !previousValue) {
      setOpenWithCommentType('pregnant');
    }
  };

  return (
    <div className="h-full w-full">
      <AspirationToolbar
        saving={h.saving}
        readyToSave={h.readyToSave}
        saveBlockedReason={h.saveBlockedReason}
        aspirationInfoLoading={h.aspirationInfoLoading}
        onSave={h.handleSubmit(h.onSubmit)}
        onCancel={h.handleCancel}
        showAspirationStats={h.showAspirationStats}
        setShowAspirationStats={h.setShowAspirationStats}
        statsDisabled={!h.opuDate || !h.opuSiteId || !h.opuTechnicianId}
        opuDate={h.opuDate}
        opuSiteId={h.opuSiteId}
      />

      <div className="relative">
        {h.formDataLoading && (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/70">
            <ProgressSpinner style={{ width: '50px', height: '50px' }} />
          </div>
        )}
        <Splitter className="aspiration-form__container">
          <SplitterPanel
            size={SPLITTER_PANEL_SIZE}
            className="aspiration__splitter-panel"
          >
            <form
              className="flex w-full flex-col"
              onSubmit={h.handleSubmit(h.onSubmit)}
            >
              <div className="flex w-full justify-between">
                <AspirationInfoPanel
                  selectedAspiration={h.selectedAspiration}
                  aspSiteName={h.aspSite?.name}
                  opuTechnicianId={h.opuTechnicianId}
                  chuteTechnicianId={h.chuteTechId}
                  opuTechnicianData={h.opuTechnicianData ?? undefined}
                  aspirationInfo={h.aspirationInfo}
                  months={h.months}
                  lastBredInterval={h.lastBredInterval}
                />
                <TopFieldsSection
                  control={h.control}
                  onPregnantChange={handlePregnantChange}
                />
              </div>

              <div className="flex w-full justify-between">
                <OvaryFieldsSection
                  control={h.control}
                  errors={h.errors}
                  leftTotal={h.leftTotal}
                  rightTotal={h.rightTotal}
                  scheduleOptions={h.scheduleOptions}
                  ovarySideFirst={h.ovarySideFirst}
                  isBrazil={h.isBrazil}
                />
                <CommentsSection
                  control={h.control}
                  Controller={Controller}
                  setValue={h.setValue}
                  getValues={h.getValues}
                  openWithCommentType={openWithCommentType}
                  onDialogOpened={() => setOpenWithCommentType(undefined)}
                />
              </div>
            </form>

            <EmbGrid mode={h.mode} rowData={h.fertData || null} />
          </SplitterPanel>

          <SplitterPanel className="aspiration__grid-container">
            <DonorScheduleGrid
              mode={h.mode}
              rowData={h.donorScheduleData || null}
            />
            <AspirationHistoryGrid
              mode={h.mode}
              rowData={h.aspirationHistoryData || null}
            />
            <ProcedureGrid mode={h.mode} rowData={h.proceduresData || null} />
          </SplitterPanel>
        </Splitter>
      </div>
    </div>
  );
};
